import type { Option } from "../../core/option.ts";
import type { DataPresetSpec } from "../../rows/data-preset-spec.ts";
import { PRODUCT_DATA_PRESETS } from "./product-data-presets.ts";
import { DBG_DATA_PRESETS } from "./dbg-data-presets.ts";

const AUTH_SCOPED_DATA_PRESET_KEYS = new Map<string, ReadonlySet<string>>([
  ["garmin", new Set(["sleep", "stress", "hrv"])],
  ["whoop", new Set(["sleep", "recovery"])],
]);

/**
 * The built-in presets, keyed by lookup key: the product presets
 * (`product-data-presets.ts`) and the debug presets (`dbg-data-presets.ts`).
 * The test presets (`:test:*`) are not built in: a test layers them with
 * `SheetRegistries.extend`, so fixture-specific data stays outside the
 * product registry.
 */
const presets: ReadonlyMap<string, readonly DataPresetSpec[]> = new Map<string, readonly DataPresetSpec[]>([
  ...PRODUCT_DATA_PRESETS,
  ...DBG_DATA_PRESETS,
]);

/**
 * Built-in chart presets by lookup key, read only — built at module load and
 * never mutated after. Package-internal: the one read path is
 * `SheetRegistries.dataPresets` (`SheetRegistries.BUILTINS.dataPresets` for
 * the built-ins, through `dataPresetsFor`); a client or a fixture adds or
 * replaces presets by layering its own table over them with
 * `SheetRegistries.extend(base, { dataPresets })`.
 */
export const DATA_PRESETS: ReadonlyMap<string, readonly DataPresetSpec[]> = presets;

/**
 * Resolve the preset specs registered for a row's `lookupKey`. Presets are
 * keyed by wildcard (e.g. `:system.host[*].health`) while a concrete-keyed
 * row carries a resolved key (`:system.host[abc].health`). Try the exact key
 * first, then fall back to normalizing every `[...]` segment to `[*]`.
 * Route-backed Garmin/Whoop rows carry an auth segment
 * (`:~garmin:auth_abc:stress`); those resolve through the legacy preset key
 * (`:~garmin:stress`) so the chart specs stay single-sourced.
 * Returns `null` when no preset matches.
 */
export function dataPresetsFor(lookupKey: string): Option<readonly DataPresetSpec[]> {
  for (const key of dataPresetLookupKeys(lookupKey)) {
    const specs = presets.get(key);
    if (specs !== undefined) return specs;
  }
  return null;
}

export function dataPresetLookupKeys(lookupKey: string): readonly string[] {
  const keys: string[] = [lookupKey];

  const wildcard = lookupKey.replace(/\[[^\]]+\]/g, "[*]");
  if (wildcard !== lookupKey) keys.push(wildcard);

  const authScoped = authScopedPresetLookupKey(lookupKey);
  if (authScoped !== null && authScoped !== undefined) keys.push(authScoped);

  return keys;
}

function authScopedPresetLookupKey(lookupKey: string): Option<string> {
  const segments = lookupKey.split(":");
  if (segments.length !== 4) return null;

  const familySegment = segments[1];
  const metric = segments[3];
  if (familySegment === undefined || metric === undefined) return null;
  if (!familySegment.startsWith("~")) return null;

  const family = familySegment.slice(1);
  const metrics = AUTH_SCOPED_DATA_PRESET_KEYS.get(family);
  if (metrics === undefined || !metrics.has(metric)) return null;

  return `:~${family}:${metric}`;
}

/** Whether any data preset matches `lookupKey` (wildcard-aware). */
export function hasDataPreset(lookupKey: string): boolean {
  return dataPresetsFor(lookupKey) !== null;
}
